import { apiGetGuessPositionDetail } from "@/api/api";
import { useEventConnect } from "@/store/eventConnect";
import { calculatePositionMetrics } from "@/utils/position";
import { calculatePredictionPayoutCentAmount } from "@/utils/price";
import { get } from "@/utils/utils";
import { useQuery } from "@tanstack/react-query";
import { BigMath } from "@yn/utils";
import { useMemo } from "react";
import { toNumber } from "../positions-panel-components/utils";

/**
 * 持仓数据获取和处理 Hook
 *
 * 功能：
 * - 从 API 获取用户的持仓数据
 * - 合并相同类型（Yes/No）的持仓
 * - 计算当前价格、盈亏等信息
 * - 结合 WebSocket 实时订单簿数据获取最佳买价
 *
 * @param guessNo - 竞猜编号
 * @param userId - 用户ID
 * @param enabled - 是否启用数据获取
 * @returns { positionItems } - 处理后的持仓列表
 */
export function usePositionsData( guessNo: string, userId: number | null, enabled: boolean ) {
    const currentGuess = useEventConnect( state => state.guesses?.[guessNo] );

    const { data: positionsData, isLoading } = useQuery( {
        queryKey: [ "guess-trade-list", guessNo, userId ],
        queryFn: async () => {
            const [ isOk, res ] = await apiGetGuessPositionDetail( {
                pageNum: 1,
                pageSize: 20,
                userId: userId ?? undefined,
                guessNo,
            } );
            if ( isOk ) return get( res, "data", {} );
            throw new Error( get( res, "msg", "Failed to load positions" ) );
        },
        enabled: !!guessNo && enabled && !!userId,
        refetchInterval: 10_000,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    } );

    const positionItems = useMemo( () => {
        const eventTradeList = get( positionsData, "eventTradeList", null ) ?? get( positionsData, "list", null ) ?? [];
        const positionList = get( positionsData, "positionList", null ) ?? [];

        const items: any[] = [];
        const normalizedEventTradeList = Array.isArray( eventTradeList ) ? eventTradeList : [];
        if ( normalizedEventTradeList.length > 0 ) {
            normalizedEventTradeList.forEach( ( eventItem: any ) => {
                const guessTradeList = get( eventItem, "guessTradeList", [] );
                ( Array.isArray( guessTradeList ) ? guessTradeList : [] ).forEach( ( guessItem: any ) => {
                    items.push( {
                        ...guessItem,
                        eventImageUrl: get( eventItem, "eventImageUrl", "" ),
                        eventTitle: get( eventItem, "eventTitle", "" ),
                    } );
                } );
            } );
        } else {
            ( Array.isArray( positionList ) ? positionList : [] ).forEach( ( positionItem: any ) => {
                items.push( {
                    ...positionItem,
                    eventImageUrl: get( positionItem, "eventImageUrl", "" ),
                    eventTitle: get( positionItem, "eventTitle", "" ),
                } );
            } );
        }
        const byType: Record<"yes" | "no", any> = { yes: null, no: null };

        items.forEach( ( item: any ) => {
            const type = get( item, "guessType", 1 ) === 1 ? "yes" : "no";
            const prev = byType[type];
            const quantity = toNumber(
                get( item, "totalQuantity", get( item, "quantity", get( item, "shares", 0 ) ) ),
            );

            if ( !prev ) {
                byType[type] = { ...item, totalQuantity: quantity };
            } else {
                const prevQty = Number( get( prev, "totalQuantity", 0 ) );
                byType[type] = { ...prev, totalQuantity: prevQty + quantity };
            }
        } );

        const buildItem = ( type: "yes" | "no" ) => {
            const raw = byType[type];
            const shares = toNumber( get( raw, "totalQuantity", 0 ) );
            const price = toNumber(
                get(
                    raw,
                    "averagePrice",
                    get( raw, "avgTradePrice", get( raw, "avgPrice", get( raw, "avgCost", 0 ) ) ),
                ),
                0,
            );
            const initialCost = toNumber( get( raw, "cost", 0 ) );
            const payout = calculatePredictionPayoutCentAmount( shares );
            const marketPrice = ( type === "yes" ? currentGuess?.yesPrice : currentGuess?.noPrice ) || 0;
            const { profit: computedProfit, profitPercent: computedProfitRate } = calculatePositionMetrics( {
                cost: initialCost,
                marketPrice,
                quantity: shares,
            } );

            return {
                key: type,
                label: get( raw, type === "yes" ? "yesDesc" : "noDesc", type === "yes" ? "Yes" : "No" ),
                eventCategory: get( raw, "eventCategory", undefined ),
                groupTitle: get( raw, "groupTitle", "" ),
                guessCategoryId: get( raw, "guessCategoryId", undefined ),
                guessGroupNo: get( raw, "guessGroupNo", "" ),
                guessNo: get( raw, "guessNo", guessNo ),
                guessType: get( raw, "guessType", type === "yes" ? 1 : 2 ),
                guessTitle: get( raw, "guessTitle", "" ),
                guessImageUrl: get( raw, "guessImageUrl", get( raw, "imageUrl", get( raw, "eventImageUrl", "" ) ) ),
                shares,
                price,
                currentPrice: marketPrice,
                profit: computedProfit,
                profitRate: computedProfitRate,
                initialCost,
                payout,
                badgeClass: type === "yes" ? "bg-[#E8F7F1] text-yes" : "bg-[#FDECEC] text-no",
                returnClass: BigMath.gte( computedProfit, 0 ) ? "text-action-accent" : "text-state-danger",
            };
        };

        const yesItem = buildItem( "yes" );
        const noItem = buildItem( "no" );

        return [ yesItem, noItem ].filter( ( item ) => item.shares > 0 );
    }, [ positionsData, guessNo ] );

    return { positionItems, isLoading };
}
